/**
 * Per-report-type SED section requirements.
 *
 * Drives the wizard's preflight freshness card: which sections must
 * be fresh before this report type runs (stale → block / refresh
 * gate), which are nice-to-have (stale → "consider refreshing"
 * nudge), and which the report type doesn't read at all (stale → no
 * mention; we don't pay to refresh sections this report ignores).
 *
 * Note on section ids: the spec mentions "land" and "nrc"; the
 * canonical SedSectionId union (types/siteEvidence.ts) calls those
 * `parcel` and `nuclearInfra` respectively. We use the canonical ids
 * so this file is grep-compatible with sedBootstrap, sedStaleness,
 * and the rest of the SED surface.
 */
import type { ReportTypeId } from './reportTypes';
import type { SedSectionId } from '../types/siteEvidence';

/**
 * Sections each report type READS from the SED. A stale or missing
 * required section blocks the wizard's normal Confirm CTA and
 * surfaces a "Refresh & generate" combined CTA instead.
 *
 * `satisfies Record<...>` enforces type drift between ReportTypeId
 * and this map at compile time.
 */
export const REPORT_TYPE_SED_REQUIREMENTS = {
  // Phase 3 part B (2026-09-07) — `waterStress` joins: the feasibility report
  // carries the water screen the retired Water Resource report used to.
  'feasibility':         ['climate', 'grid', 'parcel', 'seismic', 'regulatory', 'connectivity', 'electricity', 'technology', 'waterStress'],
  // QA 2026-06 — `seismic` added: the environmental report schema requires a
  // `seismicImpact` narrative, so the USGS seismic section must be fresh.
  'environmental':       ['environmental', 'climate', 'airQuality', 'seismic', 'regulatory', 'parcel', 'technology'],
  'test-fit':            ['parcel', 'seismic', 'regulatory'],
  'power-availability':  ['grid', 'electricity', 'solar', 'wind', 'nuclearInfra', 'regulatory'],
  'water-resource':      ['climate', 'environmental', 'parcel', 'regulatory', 'waterStress'],
  'interconnection':     ['connectivity', 'regulatory', 'parcel'],
  'supply-chain-risk':   ['siteResearch', 'grid', 'regulatory', 'technology'],
  // PSR-1 (row 0.99290) — Public Sentiment Report. Canonical PSR input
  // is the SED `publicSentiment` section emitted by PSE-3 (row 0.99289
  // SHIPPED 2026-05-19). W23-1 (row 0.99298) flipped the gate off the
  // pre-PSE-1 fallback (`siteResearch` + `regulatory`) onto the real
  // section. `siteContext` is user-authored on the site and is a PSR
  // nice-to-have, but lives as a top-level SED field rather than a
  // `SedSectionId`. Widened via W23-1 (row 0.99298) implementation plan.
  'public-sentiment':    ['publicSentiment'],
  'custom-brief':        ['grid', 'regulatory', 'electricity', 'environmental', 'siteResearch'],
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report. Anchored on the
  // power-economics evidence the deterministic project-finance engine reads:
  // grid + electricity (firm-generation cost basis / tariff), nuclearInfra
  // (firm-generation CAPEX/OPEX), regulatory (incentive context). Strict
  // subset of the registry requiredSedSections (ssot test enforces).
  'financial':           ['grid', 'electricity', 'nuclearInfra', 'regulatory'],
  'executive-summary':   ['grid', 'siteResearch', 'electricity', 'environmental'],
} satisfies Record<ReportTypeId, SedSectionId[]>;

/**
 * Sections that strengthen this report type but don't block it.
 * Surfaced as "↑ N optional sections could be refreshed" when they
 * go stale; no preflight gate.
 *
 * `Partial` so report types without optional sections can omit
 * themselves rather than carry an empty array.
 *
 * siteContext is a top-level site field, not a formal SED section, but
 * Public Sentiment needs it as an optional input. Future fields of this
 * kind can be added to the union.
 *
 * ── Why `economicEvidence` stays HERE and not in REQUIREMENTS ─────────────
 *
 * The 2026-09-03 report-integrity review proposed promoting it, on the
 * correct finding that a feasibility report can be generated with zero
 * economic / workforce / comparable evidence. Promoting it does not fix that,
 * and makes the product worse:
 *
 *   - REQUIREMENTS is a FRESHNESS gate. Its only gating consumer is
 *     `PreflightFreshnessCard`'s required-stale branch, whose sole remedy is a
 *     provider refresh — and `economicEvidence` has neither a provider nor a
 *     producer (`isProviderRefreshableSedSection` is false). With a 30-day
 *     TTL, every site older than a month would carry a warning gate whose
 *     primary CTA cannot clear it.
 *   - It gates nothing on the server. The preflight card ships a documented
 *     "Generate without refresh" escape and the enqueue path never reads this
 *     map, so no false assertion is actually prevented.
 *
 * The real defect — the prompt could not distinguish "screened and found
 * nothing" from "never screened" — is fixed at the source that matters, in
 * `sedToText`: an absent economic screen now emits an explicit NOT AVAILABLE
 * block instead of the section being dropped. A hard PREREQUISITE (the
 * `decisionProducts` vocabulary, not this map) is the right mechanism if the
 * screen should ever block generation, and that is a product decision about
 * which reports may not run at all.
 */
export const REPORT_TYPE_SED_OPTIONAL: Partial<
  Record<ReportTypeId, readonly (SedSectionId | 'siteContext')[]>
> = {
  // Site-diligence screens and professional documents strengthen the reports
  // below but must not block generation: providers can be disabled/unavailable,
  // and ALTA, mineral-title, archaeological, and geotechnical documents are
  // recommended evidence rather than universal prerequisites.
  'feasibility':        [
    'environmental', 'airQuality', 'solar', 'wind', 'nuclearInfra', 'siteResearch',
    'topography', 'geotechnical', 'survey', 'mineralResources', 'culturalResources',
    'economicEvidence',
    // STAGE 5.1 — `gridRedundancy` moved here from power-availability and reads
    // the KMZ-derived on-site infrastructure. OPTIONAL, never required: it has
    // no provider (`SED_SECTION_PROVIDERS.onSiteInfrastructure` is empty), so as
    // a REQUIRED section it would raise a preflight gate whose only offered
    // remedy — a provider refresh — cannot clear it.
    'onSiteInfrastructure',
  ],
  'environmental':      ['grid', 'connectivity', 'mineralResources', 'culturalResources', 'economicEvidence'],
  'test-fit':           ['topography', 'geotechnical', 'survey', 'mineralResources', 'culturalResources'],
  'power-availability': ['climate', 'parcel', 'economicEvidence'],
  // P6 — optional, not required: requiring it would turn the usgs-waters refdata
  // snapshot into a preflight gate for every water report.
  'water-resource':     ['economicEvidence', 'waterResource'],
  'supply-chain-risk':  ['siteContext', 'parcel', 'connectivity', 'economicEvidence'],
  'public-sentiment':   ['siteContext', 'economicEvidence'],
  'custom-brief':       [
    'climate', 'connectivity', 'parcel',
    'topography', 'geotechnical', 'survey', 'mineralResources', 'culturalResources',
    'economicEvidence',
  ],
  'executive-summary':  [
    'climate', 'connectivity', 'parcel', 'regulatory',
    'topography', 'geotechnical', 'survey', 'mineralResources', 'culturalResources',
    'economicEvidence',
  ],
  'financial':          ['economicEvidence'],
};

/**
 * Optional sections that nevertheless participate in report currency once
 * present. Economic evidence is non-blocking at generation time, but a newly
 * completed research/profile/screening revision must mark every report that
 * consumed it refresh-needed. Reports outside this map remain unaffected.
 */
export const REPORT_TYPE_SED_CURRENCY_BINDINGS = {
  feasibility: ['economicEvidence', 'publicSentiment'],
  environmental: ['economicEvidence', 'publicSentiment'],
  'power-availability': ['economicEvidence'],
  'water-resource': ['economicEvidence'],
  'supply-chain-risk': ['economicEvidence'],
  'public-sentiment': ['economicEvidence'],
  'custom-brief': ['economicEvidence'],
  'executive-summary': ['economicEvidence', 'publicSentiment'],
  financial: ['economicEvidence'],
} satisfies Partial<Record<ReportTypeId, readonly SedSectionId[]>>;

export function getReportTypeOptionalSedInputs(
  reportType: ReportTypeId,
): readonly (SedSectionId | 'siteContext')[] {
  return REPORT_TYPE_SED_OPTIONAL[reportType] ?? [];
}

/**
 * Optional SED sections whose content identity participates in report
 * currency after generation. This is intentionally narrower than the full
 * optional-input list: a useful optional source can strengthen a future run
 * without invalidating an analysis that never treated it as currency-bearing.
 */
/**
 * SED sections that NEVER decide whether a generated analysis is still current
 * (owner decision 1, 2026-09-06 — the two-stage pipeline's environmental
 * prerequisite). `airQuality` is a LIVE index refreshed on a one-day TTL: its
 * value at generation is recorded in the report like any other input, but a
 * routine AQI refresh moving that slot's fingerprint must not read a
 * day-old Environmental Assessment as non-current — that is what made
 * `environmental` unusable as an always-required report (PATH 4's
 * completeness cap re-fired on every site permanently). A section listed here
 * still participates in generation, prompts and staleness reporting; it is
 * subtracted only from the currency decision.
 */
export const REPORT_CURRENCY_VOLATILE_SED_SECTIONS: ReadonlySet<SedSectionId> = new Set<SedSectionId>([
  'airQuality',
]);

/**
 * The SED sections whose change makes a generated analysis of `reportType`
 * non-current: its canonical required sections plus its currency bindings,
 * minus the volatile live indices above. `reportSedIsCurrent` reads THIS, so
 * the exemption lives in one place.
 */
export function getReportTypeSedCurrencySections(
  reportType: ReportTypeId,
): ReadonlySet<SedSectionId> {
  const sections = new Set<SedSectionId>([
    ...getCanonicalRequiredSedSections(reportType),
    ...getReportTypeSedCurrencyBindings(reportType),
  ]);
  for (const volatile of REPORT_CURRENCY_VOLATILE_SED_SECTIONS) sections.delete(volatile);
  return sections;
}

export function getReportTypeSedCurrencyBindings(
  reportType: ReportTypeId,
): readonly SedSectionId[] {
  return (REPORT_TYPE_SED_CURRENCY_BINDINGS as Partial<
    Record<ReportTypeId, readonly SedSectionId[]>
  >)[reportType] ?? [];
}

/* ── P3-9 — single source of truth for "required SED sections" ──────────────
 *
 * The codebase carried TWO divergent required-SED lists:
 *   1. `REPORT_TYPE_REGISTRY[type].requiredSedSections` — loose `string[]`,
 *      historically hand-maintained, includes non-`SedSectionId` tokens
 *      (`flood`, `waterStress`, the PSR `siteContext`), used by the preflight /
 *      bootstrap / coverage surfaces.
 *   2. `REPORT_TYPE_SED_REQUIREMENTS` (this file) — strict `SedSectionId[]`,
 *      used by the freshness-gate / rerun / reportSectionSedDeps surfaces.
 * They drifted (different sets per type → contradictory "missing" tallies).
 *
 * `getCanonicalRequiredSedSections` is the SSOT both families should resolve
 * through: the STRICT, type-checked `REPORT_TYPE_SED_REQUIREMENTS` is canonical
 * (it is the one the compiler keeps honest via `satisfies`). The registry list
 * is retained as a loose superset only for the non-`SedSectionId` tokens; a
 * unit test (`reportTypeSedRequirements.ssot.test.ts`) asserts every strict
 * required id is also present in the registry list, so the two cannot silently
 * diverge again. Callers wanting the authoritative strict set call THIS. */
export function getCanonicalRequiredSedSections(
  reportType: ReportTypeId,
): readonly SedSectionId[] {
  return REPORT_TYPE_SED_REQUIREMENTS[reportType] ?? [];
}
